import AreaIndicator from '../common/AreaIndicator.jsx';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeaturesGrid from './FeaturesGrid.jsx';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import ProgressBackdrop from './ProgressBackdrop.jsx';
import QuotaDisplay from './QuotaDisplay.jsx';
import React, {useMemo, useState} from 'react';
import ReleaseConfirmationModal from './ReleaseConfirmationModal.jsx';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {array, bool, func, object, string} from 'prop-types';
import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import {notify} from '@actions/notifications';
import {queryKeys as productsQueryKeys} from '@api/products';
import {
  queryKeys as reservationsQueryKeys,
  useDeleteQuotaReservation,
} from '@api/quotaReservations';
import {trackEvent} from '@app/analytics';
import {useDispatch} from 'react-redux';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

const ReservationCard = ({
  product,
  reservations,
  selectedFeatureRefs,
  setSelectedFeatureRefs,
  onZoomToFeature,
  features,
  loadingFeatures,
  collectionId,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {allowReleaseQuota} = useFlags() ?? {};

  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const {mutateAsync: deleteReservation} = useDeleteQuotaReservation();

  const costsById = useMemo(() => {
    const costs = {};
    reservations?.forEach(reservation => {
      costs[reservation.aoi_ref] = {
        cost: reservation.amount,
      };
    });
    return costs;
  }, [reservations]);

  const selectionsCost = useMemo(() => {
    let cost = 0;
    selectedFeatureRefs?.forEach(featureRef => {
      cost += costsById[featureRef]?.cost || 0;
    });

    return cost;
  }, [costsById, selectedFeatureRefs]);

  const reservedFeatures = useMemo(
    () =>
      features.filter(f =>
        reservations.find(r => r.aoi_ref === getFeatureReference(f))
      ),
    [features, reservations]
  );

  const onRowClick = values => {
    setSelectedFeatureRefs(values);
  };

  const handleClose = () => {
    setOpenConfirmation(false);
    trackEvent('Reservation Card', {
      action: 'closed release confirmation modal',
    });
  };

  const onReleaseClick = async () => {
    setIsReleasing(true);

    const toRelease = reservations.filter(r =>
      selectedFeatureRefs.includes(r.aoi_ref)
    );

    const promises = toRelease.map(reservation =>
      deleteReservation({id: reservation.id, collectionId})
    );

    await Promise.allSettled(promises).then(resp => {
      const successResp = resp
        .filter(d => d.status === 'fulfilled')
        .map(d => d.value);

      const errorsResp = resp.filter(d => d.status === 'rejected');

      setIsReleasing(false);

      queryClient.invalidateQueries({queryKey: productsQueryKeys.lists()});

      if (collectionId) {
        queryClient.invalidateQueries({
          queryKey: reservationsQueryKeys.list({
            collection_id: collectionId,
          }),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: reservationsQueryKeys.lists(),
        });
      }

      if (successResp.length > 0) {
        dispatch(
          notify({
            message: `Successfully released quota for ${product.title}`,
            persist: true,
          })
        );

        trackEvent('Reservation Card', {
          action: 'released quota successfully',
          productTitle: product.title,
          totalReservations: successResp.length,
        });
      }

      if (errorsResp.length > 0) {
        dispatch(
          notify({
            message: `There was an error while releasing ${successResp.length > 0 ? 'some ' : ''}reservations for ${product.title}`,
            severity: 'error',
            persist: true,
          })
        );
      }
    });
  };

  return (
    <>
      <Card elevation={2}>
        <CardContent>
          <Grid container rowSpacing={1} direction="column">
            <Stack
              direction="row"
              sx={{
                width: '100%',
                marginBottom: 1,
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" display="inline">
                {product.title}
                <Tooltip
                  title={`Access dates: ${t('common:date_ranges.toRange', {
                    startDate: product?.term_start_date,
                    endDate: product?.term_end_date,
                  })}`}
                  placement="top"
                >
                  <CalendarIcon
                    color="action"
                    sx={{
                      marginTop: -0.5,
                      verticalAlign: 'middle',
                      marginLeft: 1,
                    }}
                  />
                </Tooltip>
              </Typography>

              {allowReleaseQuota && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={isReleasing}
                  onClick={() => setOpenConfirmation(true)}
                  sx={{marginLeft: 'auto', flexShrink: 0}}
                >
                  {isReleasing ? 'Releasing...' : 'Release quota'}
                </Button>
              )}
            </Stack>

            <Grid container direction="row" xs={12} alignItems="center">
              <Grid xs={8}>
                <Typography>Current balance</Typography>
              </Grid>
              <Grid xs={4}>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  textAlign="right"
                >
                  <QuotaDisplay
                    quota={product.quota_total - product.quota_used}
                    isUnlimited={product.unlimited_quota}
                  />
                </Typography>
              </Grid>
            </Grid>
            <Grid container direction="row" xs={12} alignItems="center">
              <Grid xs={8}>
                <Typography>To release</Typography>
              </Grid>
              <Grid xs={4}>
                <Typography textAlign="right">
                  <AreaIndicator squareMeters={selectionsCost} />
                </Typography>
              </Grid>
            </Grid>
            {!product.unlimited_quota && (
              <>
                <Grid container direction="row" xs={12} sx={{marginY: 1}}>
                  <Grid xs={9}></Grid>
                  <Grid xs={3}>
                    <Divider flexItem />
                  </Grid>
                </Grid>
                <Grid container direction="row" xs={12} alignItems="center">
                  <Grid xs={8}>
                    <Typography>New balance</Typography>
                  </Grid>
                  <Grid xs={4}>
                    <Typography textAlign="right">
                      <AreaIndicator
                        squareMeters={
                          product.quota_total -
                          product.quota_used +
                          selectionsCost
                        }
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
        <Divider sx={{marginTop: 1}} />
        <CardActions>
          <IconButton
            size="small"
            disabled={loadingFeatures}
            onClick={() => setFeaturesExpanded(prevState => !prevState)}
            data-testid="features-table-expand-button"
            sx={{
              transform: !featuresExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: theme =>
                theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          >
            {loadingFeatures ? (
              <CircularProgress size={24} />
            ) : (
              <ExpandMoreIcon />
            )}
          </IconButton>
          <Typography variant="subtitle1">
            Features {loadingFeatures ? '' : `(${reservedFeatures.length})`}
          </Typography>
        </CardActions>
        <Collapse
          in={featuresExpanded}
          timeout="auto"
          unmountOnExit
          sx={{
            '.MuiCollapse-wrapper': {
              // keeps data grid happy and not janky
              height: '100%',
              minHeight: 250,
              maxHeight: 500,
            },
          }}
        >
          <FeaturesGrid
            features={reservedFeatures}
            estimates={costsById}
            rowSelectionModel={allowReleaseQuota ? selectedFeatureRefs : []}
            onRowSelectionModelChange={onRowClick}
            loading={loadingFeatures}
            disableSelection={isReleasing}
            onZoomToClicked={onZoomToFeature}
            hideSelection={!allowReleaseQuota}
          />
        </Collapse>
      </Card>
      <ProgressBackdrop open={isReleasing} title="Releasing quota" />
      <ReleaseConfirmationModal
        open={openConfirmation}
        onReleaseClick={onReleaseClick}
        isReleasing={isReleasing}
        onClose={handleClose}
      />
    </>
  );
};

ReservationCard.propTypes = {
  features: array,
  product: object,
  loadingFeatures: bool,
  reservations: array,
  selectedFeatureRefs: array,
  setSelectedFeatureRefs: func,
  onZoomToFeature: func,
  collectionId: string,
};

export default ReservationCard;
